import { MouseEvent, useContext } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'motion/react';

import {
  MenuContext,
  MenuTriggerContext,
} from '@components/primitives/Menu/context';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import styles from './styles.module.css';

type MenuListProps = DropdownMenu.DropdownMenuContentProps & {
  container?: DropdownMenu.DropdownMenuPortalProps['container'];
};

export function MenuList({
  className = '',
  children,
  align = 'end',
  container,
  ...props
}: MenuListProps) {
  const [open] = useContext(MenuContext);
  const triggerRef = useContext(MenuTriggerContext);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <AnimatePresence>
      {open && (
        <DropdownMenu.Portal
          forceMount
          container={
            container ?? triggerRef?.current?.closest("[role='dialog']")
          }
        >
          <DropdownMenu.Content
            asChild
            align={align}
            sideOffset={5}
            collisionPadding={10}
            {...props}
          >
            <motion.div
              {...ANIMATIONS}
              className={cn(styles.menu, className)}
              onClick={handleClick}
            >
              <AnimatePresence initial={false} mode="sync">
                {children}
              </AnimatePresence>
            </motion.div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
    </AnimatePresence>
  );
}

const ANIMATIONS = {
  initial: {
    opacity: 0,
    scale: 0.97,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.1 },
  },
  transition: {
    duration: 0.2,
    ease: easeOutSmooth,
  },
};
